// © Microsoft Corporation. All rights reserved.

import { FluentButton, FluentDialog, FluentDivider } from "@fluentui/web-components";
import { ViewTemplate, html, when, ref } from "@microsoft/fast-element";
import { telemetry } from "@fast/components";
import { MsnMarketSelector } from "@msnews/msn-market-selector";

import { Settings } from "./Settings";
import { IconSvgs } from "@msnews/wc-shared";

// Tree-shaking
FluentButton;
FluentDivider;
MsnMarketSelector;
FluentDialog;

const marketSelectorTemplate = html<Settings>`
<div class="language-dropdown">
    <msn-market-selector
        :id="${x => x.marketSelectorProps.id}"
        :onValueChange="${x => x.marketSelectorProps.onValueChange}"
        :defaultMarketId="${x => x.marketSelectorProps.defaultMarketId}"
        :selectedMarketId="${x => x.selectedMarketId}"
        :marketList="${x => x.marketSelectorProps.marketList}"
        :label="${x => x.marketSelectorProps.label}"
        :marketSelectorMetaTag="${x => x.marketSelectorProps.marketSelectorMetaTag}"
        @change=${x => x.toggleSettingsDialog(false)}
    ></msn-market-selector>
</div>
<fluent-divider role="presentation"></fluent-divider>
`;

const settingsDialogTemplate = html<Settings>`
<fluent-dialog
    class="settings-dialog ${x => x.hideMarketSelector && "without-market-selector"}"
    @dismiss=${x => x.toggleSettingsDialog(false)}
>
    <div class="settings-dialog-header">
        <span class="settings-dialog-title">
            ${x => x.localizedStrings.settingsDialogTitle || null}
        </span>
        <fluent-button
            class="settings-close-button"
            appearance="stealth"
            title="${x => x.localizedStrings.settingsCloseButtonTooltip || null}"
            aria-label="${x => x.localizedStrings.settingsCloseButtonTooltip || null}"
            @click=${x => x.toggleSettingsDialog(false)}
            tabindex="0"
            ${telemetry(x => x.telemetryContext && x.telemetryContext.settingsCloseButtonMetaTag)}
            ${ref("closeButton")}
        >
            ${IconSvgs.closeButton}
        </fluent-button>
    </div>
    <fluent-divider role="presentation"></fluent-divider>
    ${when(x => !x.hideMarketSelector, marketSelectorTemplate)}
    <div class="personalize-settings">
        ${x => x.localizedStrings.settingsDialogPersonalizeLabel || null}
        <fluent-button
            class="personalize-settings-button"
            appearance="stealth"
            @click=${x => x.onPersonalizeClick()}
            title="${x => x.localizedStrings.settingsDialogPersonalizeButtonText || null}"
            aria-label="${x => x.localizedStrings.settingsDialogPersonalizeButtonText || null}"
            tabindex="0"
            ${telemetry(x => x.telemetryContext && x.telemetryContext.personalizeButtonMetaTag)}
        >
            ${x => x.localizedStrings.settingsDialogPersonalizeButtonText}
        </fluent-button>
    </div>
</fluent-dialog>
`;

export const SettingsTemplate: ViewTemplate<Settings> = html<Settings>`
<style>${x => x.settingsOverrideStyles}</style>
<fluent-button
    class="settings-button"
    appearance="stealth"
    @click=${x => x.toggleSettingsDialog(!x.isSettingsDialogVisible)}
    title="${x => x.localizedStrings.settingsButtonTooltip || null}"
    aria-label="${x => x.localizedStrings.settingsButtonTooltip || null}"
    ${telemetry(x => x.telemetryContext && x.telemetryContext.settingsMetaTag)}
    aria-expanded="${x => x.isSettingsDialogVisible ? "true" : "false"}"
    ${ref("settingsButton")}
>
    ${IconSvgs.settingsGearIcon}
</fluent-button>
${when(x => x.isSettingsDialogVisible, settingsDialogTemplate)}
`;
